// faceMatch.js — the kiosk's matching rule. Kept in lockstep with
// backend/src/services/faceMatch.js, which is the source of truth; the server
// also sends its thresholds down with GET /attendance/kiosk/descriptors and the
// kiosk uses those over the defaults here.
//
// Why a margin and not just a threshold: a nearest-neighbour rule flips between
// two close candidates from one frame to the next, and with a per-user cooldown
// the runner-up gets marked on the very next scan — one face, two people
// checked in. The winner must beat the next PERSON by a clear margin or the
// scan is reported as ambiguous and nobody is marked.
export const DEFAULT_THRESHOLDS = { threshold: 0.5, margin: 0.08 };

export function euclidean(a, b) {
  if (!a || !b || a.length !== b.length) return Infinity;
  let sum = 0;
  for (let i = 0; i < a.length; i += 1) {
    const d = a[i] - b[i];
    sum += d * d;
  }
  return Math.sqrt(sum);
}

/**
 * @param {Float32Array|number[]} probe
 * @param {Array<{userId:string,name:string,descriptors:Array<Float32Array|number[]>}>} people
 */
export function bestMatch(probe, people, thresholds = DEFAULT_THRESHOLDS) {
  const threshold = thresholds.threshold ?? DEFAULT_THRESHOLDS.threshold;
  const minMargin = thresholds.margin ?? DEFAULT_THRESHOLDS.margin;

  let best = null, bestD = Infinity, second = null, secondD = Infinity;
  for (const person of people || []) {
    let d = Infinity;
    for (const desc of person.descriptors || []) {
      const x = euclidean(probe, desc);
      if (x < d) d = x;
    }
    if (d < bestD) {
      second = best; secondD = bestD;
      best = person; bestD = d;
    } else if (d < secondD) {
      second = person; secondD = d;
    }
  }

  const margin = Number.isFinite(secondD) ? secondD - bestD : Infinity;
  const underThreshold = !!best && bestD <= threshold;
  const ambiguous = underThreshold && margin < minMargin;
  return {
    user: best ? { userId: best.userId, name: best.name } : null,
    distance: bestD,
    runnerUp: second ? { userId: second.userId, name: second.name } : null,
    runnerUpDistance: secondD,
    margin,
    matched: underThreshold && !ambiguous,
    ambiguous,
  };
}

/** Flat server rows → per-person groups (all of a person's shots together). */
export function groupByUser(rows) {
  const map = new Map();
  for (const r of rows || []) {
    const entry = map.get(r.userId) || { userId: r.userId, name: r.name, descriptors: [] };
    entry.descriptors.push(r.descriptor instanceof Float32Array ? r.descriptor : new Float32Array(r.descriptor));
    map.set(r.userId, entry);
  }
  return [...map.values()];
}
